"use client";

import clsx from "clsx";
import {
  Autocomplete,
  Button,
  Input,
  Popover,
  ListBox,
  ListBoxItem,
  SearchField,
  Select as AriaSelect,
} from "react-aria-components";
import { useFilter } from "react-aria";
import CountryFlag from "@ui/components/languages/CountryFlag";
import ChevronDown16 from "@ui/icons/16x16/chevron-down-16.svg";
import { X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FieldContainer } from "./FieldContainer";
import { FieldLabel } from "./FieldLabel";
import { ScrollFadeOverlay } from "./ScrollFadeOverlay";
import {
  fieldErrorClassname,
  phoneInputContainerClassname,
  phoneInputCountryButtonClassname,
  phoneInputFieldClassname,
  selectContentClassname,
  selectItemStateClassname,
  selectSearchFieldWrapperClassname,
} from "./field-variants";
import type { BaseFieldProps } from "./Input";
import { useFieldContext } from "../hooks/form-context";
import { useScrollFade } from "../hooks/useScrollFade";
import { COUNTRY_DATA } from "@mw/shared/countryData";
import { useTranslations } from "next-intl";

export type PhoneInputProps = BaseFieldProps & {
  country?: string;
  /** When true, hides phone code on mobile and shows only the flag with a narrower trigger */
  compactOnMobile?: boolean;
  showTestVariant?: boolean;
};

const extractNationalNumber = (value: string, phoneCode: string): string => {
  if (!value) return "";

  if (value.startsWith(phoneCode)) {
    // Remove country code prefix and trim leading whitespace only
    return value.slice(phoneCode.length).replace(/^\s+/, "");
  }

  // Fallback: strip any leading + and digits (old format or edge cases)
  return value.replace(/^\+?\d*\s*/, "").trim();
};

export const PhoneInput = ({
  label,
  placeholder,
  required,
  name,
  country,
  compactOnMobile,
  showTestVariant,
}: PhoneInputProps) => {
  const field = useFieldContext();
  const t = useTranslations("ClientKeys.contact_sales_page.form");
  const [selectedCountryValue, setSelectedCountryValue] = useState(country || "United States of America");
  const [hasManuallyInteracted, setHasManuallyInteracted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const listBoxRef = useRef<HTMLDivElement>(null);
  const { canScrollUp, canScrollDown, updateScrollState, resetScrollState, initScrollState } =
    useScrollFade(listBoxRef);

  const { contains } = useFilter({ sensitivity: "base" });

  useEffect(() => {
    if (country && !hasManuallyInteracted) {
      setSelectedCountryValue(country);
    }
  }, [country, hasManuallyInteracted]);

  const countries = COUNTRY_DATA;

  // Find the selected country from the full list to preserve display while searching
  const selectedCountry = useMemo(
    () => countries.find(c => c.value === selectedCountryValue) || countries[0],
    [countries, selectedCountryValue],
  );

  const listBoxItems = useMemo(() => countries.map(c => ({ id: c.value, ...c })), [countries]);

  const phoneValue = (field.state.value as string) || "";
  const nationalNumber = extractNationalNumber(phoneValue, `+${selectedCountry.phone_code}`);

  const handleCountryChange = (key: React.Key | null) => {
    if (key === null) return;
    const countryCode = key as string;
    const country = countries.find(c => c.value === countryCode) || countries[0];
    setHasManuallyInteracted(true);
    setSelectedCountryValue(countryCode);
    const newValue = nationalNumber ? `+${country.phone_code}${nationalNumber}` : "";
    field.handleChange(newValue);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // Empty or whitespace-only clears the field
    if (!rawValue.trim()) {
      field.handleChange("");
      return;
    }

    setHasManuallyInteracted(true);
    // Store with country code prefix, preserving user's formatting
    field.handleChange(`+${selectedCountry.phone_code}${rawValue}`);
  };

  const labelElement = (
    <FieldLabel label={label} htmlFor={name} required={required} showTestVariant={showTestVariant} />
  );

  const errorElement = !field.state.meta.isValid &&
    field.state.meta.isBlurred &&
    field.state.meta.errors.length > 0 && (
      <div className={fieldErrorClassname} role='alert'>
        {field.state.meta.errors[0]}
      </div>
    );

  const emptyStateElement = (
    <div className='tw-px-3 tw-py-3 tw-text-sm tw-text-center text-light'>{t("empty_states.no_countries_found")}</div>
  );

  const listBoxContent = (
    <ListBox
      ref={listBoxRef}
      className='tw-max-h-64 tw-overflow-y-auto tw-overflow-x-hidden tw-outline-none tw-pt-1'
      items={listBoxItems}
      onScroll={updateScrollState}
      renderEmptyState={() => emptyStateElement}
    >
      {item => (
        <ListBoxItem
          id={item.value}
          textValue={`${item.label} +${item.phone_code}`}
          className={selectItemStateClassname}
        >
          <div className='tw-flex tw-items-center tw-gap-2 tw-overflow-hidden'>
            <CountryFlag
              countryCode={item.flag_code}
              alt={`${item.label} flag`}
              className='tw-shrink-0 tw-rounded-full'
            />
            <span className='tw-flex-1 tw-truncate'>{item.label}</span>
            <span className='tw-text-sm tw-font-mono tw-shrink-0 text-light'>+{item.phone_code}</span>
          </div>
        </ListBoxItem>
      )}
    </ListBox>
  );

  return (
    <FieldContainer>
      {labelElement}
      <div ref={containerRef} className={phoneInputContainerClassname}>
        <AriaSelect
          value={selectedCountryValue}
          isOpen={isOpen}
          onChange={handleCountryChange}
          onOpenChange={open => {
            setIsOpen(open);
            if (open) {
              // Measure container width when opening (not during render)
              setPopoverWidth(containerRef.current?.offsetWidth);
              // Set initial scroll state: start at top, always scrollable with 200+ countries
              initScrollState(true);
            } else {
              resetScrollState();
            }
          }}
          aria-label='Select country code'
          className='tw-h-full tw-self-stretch'
        >
          <Button className={phoneInputCountryButtonClassname(compactOnMobile)}>
            <span
              className={clsx(
                "tw-flex tw-items-center tw-gap-2 tw-overflow-hidden",
                compactOnMobile ? "tw-w-auto sm:tw-w-20" : "tw-w-20",
              )}
            >
              <CountryFlag
                countryCode={selectedCountry.flag_code}
                alt={`${selectedCountry.label} flag`}
                className='tw-shrink-0 tw-rounded-full'
              />
              <span className={clsx("tw-text-sm tw-font-mono text-light", compactOnMobile && "tw-hidden sm:tw-inline")}>
                +{selectedCountry.phone_code}
              </span>
            </span>
            <ChevronDown16
              className={clsx(
                "tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-transition-transform tw-duration-200 text-lighter",
                {
                  "tw-rotate-180": isOpen,
                },
              )}
            />
          </Button>
          <Popover
            className={clsx(selectContentClassname, "tw-flex tw-flex-col")}
            offset={4}
            placement='bottom'
            containerPadding={0}
            triggerRef={containerRef}
            style={{ width: popoverWidth }}
          >
            <Autocomplete filter={contains}>
              <SearchField
                aria-label={t("placeholders.search_countries")}
                className='tw-px-3 tw-pt-3 tw-pb-3'
                onChange={() => setTimeout(updateScrollState, 0)}
              >
                <div className={selectSearchFieldWrapperClassname}>
                  <Input
                    placeholder={t("placeholders.search_countries")}
                    className='tw-flex-1 tw-min-w-0 tw-border-none tw-outline-none tw-bg-transparent tw-text-sm placeholder:tw-text-gray-400 [&::-webkit-search-cancel-button]:tw-hidden'
                  />
                  <Button className='tw-p-0.5 tw-rounded tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-black/5 tw-transition-colors group-empty:tw-invisible'>
                    <X className='tw-size-3' aria-hidden />
                  </Button>
                </div>
              </SearchField>
              <div className='tw-border-t border-default' />
              <ScrollFadeOverlay canScrollUp={canScrollUp} canScrollDown={canScrollDown}>
                {listBoxContent}
              </ScrollFadeOverlay>
            </Autocomplete>
          </Popover>
        </AriaSelect>
        <div className='tw-w-px tw-h-full tw-bg-gray-200 tw-shrink-0' />
        <input
          className={phoneInputFieldClassname}
          type='tel'
          id={name}
          name={name}
          placeholder={showTestVariant ? undefined : placeholder}
          required={required}
          value={nationalNumber}
          onChange={handlePhoneChange}
          onBlur={field.handleBlur}
          autoComplete='off'
        />
      </div>
      {errorElement}
    </FieldContainer>
  );
};

export const FieldPhoneNumber = PhoneInput;
